import {
  formatCents,
  formatOneInChance,
  formatProbabilityPercent,
  formatTermDays,
} from '../lib/format';
import { miningModeLabel } from '../lib/miningModes';
import type { Order } from '../lib/api';

/**
 * The frozen order, as it will be paid for.
 *
 * ===============================================================================
 * THIS IS NOT A QUOTE. IT IS A RECEIPT FOR A DECISION ALREADY MADE.
 * ===============================================================================
 *
 * Every figure comes from the order the server persisted and from its immutable
 * `purchase_snapshot`. Nothing here re-quotes, re-prices, re-reads the catalogue, or
 * recomputes a discount from today's promo-code definitions. If Sidney edits a rate a
 * minute from now, this screen must still describe what THIS customer bought.
 *
 * The component therefore does no commercial arithmetic at all. It formats integers the
 * server already decided — there is not a single multiplication or subtraction below, and
 * that is deliberate: a figure the browser derived is a figure the server never agreed to.
 *
 * ---------------------------------------------------------------------------
 * HOW THE PRICING LADDER RECONCILES, AND WHY NOTHING IS DOUBLE-COUNTED
 *
 *   Reference price   what this hashrate and term would have cost at the smallest band
 *                     and shortest term. Frozen at purchase.
 *   Your price        the band/term price actually charged  (subtotalCents)
 *   Promo             the code's authoritative amount        (discountAmountCents)
 *   ─────────────
 *   Total             subtotal − promo, exactly              (totalCents)
 *
 *   Total saved       reference − total, and therefore ALREADY INCLUDING the promo.
 *
 * The promo line explains the total; it is never added to "Total saved" a second time.
 * All four values are read, never computed, so the column cannot fail to add up.
 */

/** One label/value row. Wraps rather than scrolls, which is what keeps mobile clean. */
function Row({
  label,
  value,
  testId,
  tone,
  emphasis,
}: {
  label: string;
  value: React.ReactNode;
  testId?: string;
  tone?: 'saving';
  emphasis?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <dt className={emphasis ? 'mm-label m-0' : 'mm-help'}>{label}</dt>
      <dd
        className={[
          'mm-numeric m-0 text-right',
          emphasis ? 'text-lg font-semibold' : 'text-sm font-medium',
          // The established green treatment for a saving, and only for a saving.
          // The exact class Configure's Order Summary uses, so the green matches.
          tone === 'saving' ? 'mm-summary-saving' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={testId}
      >
        {value}
      </dd>
    </div>
  );
}

/** Enough of the address to recognise, not enough to publish. Mirrors the server. */
function maskPayoutAddress(address: string): string {
  if (typeof address !== 'string' || address.length <= 14) return address;
  return `${address.slice(0, 8)}…${address.slice(-6)}`;
}

export default function OrderReviewSummary({
  order,
  /**
   * The address this order belongs to, shown so the customer recognises it before paying.
   *
   * A PROP, BECAUSE IT IS NOT ON THE ORDER DTO. `GET /api/orders/:n` deliberately omits
   * the customer's email -- an order number is enough to read that route, and publishing
   * the address there would hand it to anyone holding a reference. The review screen has
   * it because it just created the order, so it passes it in rather than the card
   * reaching for something the API does not return.
   *
   * Optional: a caller without one simply renders no email line.
   */
  email = null,
}: {
  order: Order;
  email?: string | null;
}) {
  /*
   * DEFENSIVE ON PURPOSE, and covered by an existing test.
   *
   * A created order should always carry pricing, configuration and payout -- but this
   * screen stands between a customer and a payment, and a missing field must degrade to
   * a quieter card rather than an error boundary that hides the CTA. Each section is
   * therefore omitted independently when its data is absent.
   */
  const pricing = order.pricing ?? null;
  const configuration = order.configuration ?? null;
  const payout = order.payout ?? null;
  const currency = pricing?.currency ?? 'USD';

  // Present only when the customer actually applied one. No empty row, no "None".
  const promoCode = pricing?.discountCode ?? null;
  const hasPromo = Boolean(promoCode) && (pricing?.discountAmountCents ?? 0) > 0;

  /*
   * Null for orders placed before savings were frozen. Omitted rather than recomputed:
   * the only way to produce the figure now would be to price against today's catalogue,
   * which is exactly what this screen must not do.
   */
  const savings = pricing?.savings ?? null;
  const hasSavings = Boolean(savings && savings.totalSavedCents > 0);

  /*
   * ===========================================================================
   * WHICH PRICING ROWS ARE WORTH SHOWING.
   *
   * Every figure below is authoritative and frozen; NOTHING here computes, re-prices or
   * rounds anything. The only decision is which of them say something the customer does
   * not already know, because the ladder printed the same number three times on an order
   * with no discount of any kind -- which is what made Sidney ask what "Reference price"
   * and "Your price" meant.
   *
   * DECIDED ON INTEGER CENTS, NEVER ON FORMATTED STRINGS. Two amounts that render
   * identically at two decimal places are not necessarily equal, and a presentation rule
   * keyed on display text would be wrong in exactly the cases money matters.
   *
   * THE TWO KINDS OF SAVING ARE DISTINGUISHED, and conflating them is the trap:
   *
   *   BUILT-IN  tier and duration pricing. Visible as reference > subtotal, where
   *             subtotal is the price BEFORE any promo code.
   *   PROMO     an explicit code. Visible as a code plus a positive discount amount.
   *
   * A promo-only order has total < reference and NO built-in saving, so testing the
   * final total against the reference would misclassify it and print a "Your price" row
   * identical to the reference above it. The comparison is against the SUBTOTAL.
   */
  const referenceCents = savings?.referenceSubtotalCents ?? null;
  const subtotalCents = pricing?.subtotalCents ?? 0;

  /** Tier/duration pricing brought the price down before any code was applied. */
  const hasBuiltInSavings = referenceCents !== null && referenceCents > subtotalCents;

  /*
   * The comparison price earns its row whenever something was actually taken off --
   * built-in or promo. With neither, it equals the total and is noise.
   */
  const showReference = referenceCents !== null && (hasBuiltInSavings || hasPromo);

  /*
   * The price after built-in pricing and before the code.
   *
   * Shown when it is a THIRD number: built-in savings make it differ from the reference
   * above. On a promo-only order it equals the reference exactly, so it is dropped --
   * that is the redundant row Sidney flagged. It also appears when a promo exists but no
   * reference is recorded at all, because then it is the only statement of what the code
   * came off; there it is honestly labelled "Subtotal" rather than "Your price".
   */
  const showPreDiscountPrice = hasBuiltInSavings || (referenceCents === null && hasPromo);

  const odds = order.oddsAtPurchase;
  const oddsAvailable = odds?.available === true;

  return (
    <section className="mm-panel-raised mt-8 px-5 py-5 sm:px-6" data-testid="order-review">
      <h2 className="mm-eyebrow">Your order</h2>

      {/* The headline: what they bought, and what it costs. Everything else supports it. */}
      {configuration && (
        <>
          <p className="mm-display mt-3 text-2xl" data-testid="review-headline">
            {configuration.tier?.displayHashrate ?? `${configuration.hashrateTh} TH/s`} for{' '}
            {formatTermDays(configuration.termDays)}
          </p>
          <p className="mm-help mt-1" data-testid="review-mode">
            {miningModeLabel(configuration.miningMode)}
          </p>
        </>
      )}

      {pricing && (
        <p className="mm-display mt-5 text-3xl" data-testid="review-total">
          {formatCents(pricing.totalCents, currency)}
        </p>
      )}
      {pricing?.effective && (
        <p className="mm-help mt-1" data-testid="review-effective">
          {formatCents(pricing.effective.centsPerDay, currency)} per day
        </p>
      )}

      {/* --- How that total was reached ------------------------------------ */}
      {pricing && (
      <dl className="mt-5 grid gap-2 border-t pt-4" style={{ borderColor: 'var(--mm-border)' }}>
        {showReference && referenceCents !== null && (
          <Row
            label="Reference price"
            /*
              STRUCK THROUGH, so it reads at a glance as the price NOT being charged.
              A Tailwind utility and the existing muted token -- no new style, no CSS.
              The label already says what it is; the rule is reinforcement, not the
              only cue, which is why losing it to a stylesheet would cost nothing.
            */
            value={
              <span className="line-through" style={{ color: 'var(--mm-text-subtle)' }}>
                {formatCents(referenceCents, currency)}
              </span>
            }
            testId="review-reference"
          />
        )}
        {showPreDiscountPrice && (
          <Row
            label={hasBuiltInSavings ? 'Your price' : 'Subtotal'}
            value={formatCents(pricing.subtotalCents, currency)}
            testId="review-subtotal"
          />
        )}
        {hasPromo && (
          <>
            <Row label="Promo code" value={promoCode} testId="review-promo-code" />
            <Row
              label="Promo discount"
              value={`−${formatCents(pricing.discountAmountCents, currency)}`}
              testId="review-promo-discount"
              tone="saving"
            />
          </>
        )}
        <Row
          label="Total"
          value={formatCents(pricing.totalCents, currency)}
          testId="review-total-row"
          emphasis
        />
        {hasSavings && savings && (
          /*
           * Already inclusive of the promo above — see the header. Stated once, as the
           * single answer to "how much did I save", so the two figures cannot be read as
           * additive.
           */
          <Row
            label="Total saved"
            // Rounded exactly as Configure rounds it, so the two screens never differ
            // by a percentage point on the same order.
            value={`${formatCents(savings.totalSavedCents, currency)} (${Math.round(
              savings.totalSavedBasisPoints / 100,
            )}%)`}
            testId="review-total-saved"
            tone="saving"
          />
        )}
      </dl>
      )}

      {/* --- What it is pointed at ----------------------------------------- */}
      <dl className="mt-4 grid gap-2 border-t pt-4" style={{ borderColor: 'var(--mm-border)' }}>
        {payout?.address && (
        <Row
          label="Payout address"
          value={
            <span className="font-mono text-xs" data-testid="review-payout">
              {maskPayoutAddress(payout.address)}
            </span>
          }
        />
        )}
        {oddsAvailable && odds.available && (
          <Row
            label="Odds at purchase"
            testId="review-odds"
            value={
              <>
                {formatProbabilityPercent(odds.probability)}
                {formatOneInChance(odds.oneInChance) ? (
                  <span className="mm-help"> · {formatOneInChance(odds.oneInChance)}</span>
                ) : null}
              </>
            }
          />
        )}
      </dl>

      {/* Secondary metadata: the customer cares about the order far more than its
          reference, so it sits last and quiet -- but it stays visible, because it is
          what they will quote if they contact support. */}
      <div className="mt-4 border-t pt-3" style={{ borderColor: 'var(--mm-border)' }}>
        <p className="mm-help">
          Order <span className="mm-numeric font-medium">{order.orderNumber}</span>
        </p>

        {/*
          ===========================================================================
          THE ADDRESS THAT OWNS THIS ORDER, INSIDE THE CARD RATHER THAN BESIDE IT.

          IT WAS A PANEL OF ITS OWN and cost about 8rem, which re-created the desktop
          scrollbar the spacing pass had just removed. As a continuation of the order
          metadata it costs roughly a third of that: it reuses this block's border, its
          padding and its top margin, and adds only its own three lines.

          THE FAILURE IT PREVENTS is unchanged. A typo that is still a well-formed
          address -- "jhon@" for "john@" -- passes every check on both sides, because
          format validation cannot know who the customer is. Verifying ownership before
          payment was considered and declined so checkout stays seamless, so the
          safeguard is that the customer SEES the address and recognises it.

          THERE IS NO EDIT ACTION, and that is not an omission. By this point the order
          is persisted and the email IS the customer it belongs to, so changing it is a
          change of ownership: no endpoint does it, and re-submitting the form would mint
          a second order and a second discount reservation. For a signed-in customer it
          is immutable by design -- the session's address is authoritative. Correction is
          safe one screen earlier, in the configurator, where the field is still editable
          and no order exists yet.
        */}
        {email && (
          <div className="mt-3" data-testid="order-email">
            {/*
              THE SAME ROW TREATMENT AS EVERY OTHER PIECE OF ORDER METADATA -- label
              left, value right -- rather than a label stacked above its value. That is
              one line saved on a page that has to fit a desktop viewport, and it reads
              as part of the card instead of as something appended to it.

              Row already wraps rather than compresses: flex-wrap with gap-y-1, so on a
              narrow screen the address drops to its own line at full width instead of
              being squeezed against the label. A break-all rule handles the address that is
              long even then. Neither case truncates or scrolls -- a typo the customer
              cannot see is the one failure this block exists to prevent.
            */}
            <dl className="m-0">
              <Row
                label="Email for order access"
                value={<span className="break-all">{email}</span>}
                testId="order-email-address"
              />
            </dl>

            {/* The card's own divider treatment, at its tightest: 0.375rem either side
                rather than the 0.75-1rem the section rules use, so it separates the
                address from its explanation without lengthening the page. */}
            <p
              className="mm-help mt-1.5 border-t pt-1.5"
              style={{ borderColor: 'var(--mm-border)' }}
            >
              Order updates and your secure access link will be sent here.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
